import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { AirlineDropdownInput } from "flights/AirlineDropdownInput";
import { AirportDropdownInput } from "flights/AirportDropdownInput";
import { FlightDesignatorInput } from "flights/FlightDesignatorInput";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useCallback, useEffect } from "react";
import { getAppointmentPhase } from "utilities";
import { useOpenSidePanel } from "../SidePanel";
import { Tabs } from "components/Tabs/Tabs";
import { FlightLookup } from "flights/lookup/FlightLookup";
import { useOpenFlightSidePanel } from "./FlightSidePanel";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { isFlightNumberOnly } from "utilities/flightUtils";

interface FlightNewInfo {
  artist: server.dto.ArtistSmall | null
  passengers: server.dto.ContactSmallV2[]
  phase: server.dto.AppointmentPhase

  travelAgency: string
  notes: string
  airline: server.dto.Airline
  bookingCode: string

  flightDesignator: string

  arrivalAirport: server.dto.Airport
  arrivalLocal: moment.Moment
  arrivalTerminal: string

  departureAirport: server.dto.Airport
  departureLocal: moment.Moment
  departureTerminal: string

  access: string
  capacity: number
  confirmationNumber: string
}

type NewFlightTabs = "lookup" | "commercial" | "privateJet";

const NewFlightSidePanel = () => {
  const planningContext = React.useContext(PlanningContext);
  const [selectedTab, setSelectedTab] = React.useState<NewFlightTabs>("lookup");
  const openFlightDetailSidePanel = useOpenFlightSidePanel();

  const [value, setValue] = React.useState<FlightNewInfo>({
    artist: planningContext.opts?.artist || null,
    passengers: [],
    phase: getAppointmentPhase("confirmed"),

    travelAgency: "",
    notes: "",
    airline: null,
    flightDesignator: "",
    bookingCode: "",

    arrivalAirport: null,
    arrivalLocal: planningContext.opts?.date || moment.utc(),
    arrivalTerminal: "",

    departureAirport: null,
    departureLocal: planningContext.opts?.date || moment.utc(),
    departureTerminal: "",

    access: "",
    capacity: null,
    confirmationNumber: "",
  });

  useEffect(() => {
    const fetchTravellers = async () => {
      if (value.artist?.artistId) {
        const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
          companyId: value.artist.artistId,
          defaultSelect: true,
          isActive: true,
          page: 1,
          perPage: 1000,
        });

        if (result.data.hasErrors) {
          return;
        }

        onChange({
          passengers: result.data.data.map(c => ({
            contactId: c.contact.contactId,
            culture: c.contact.culture,
            gender: c.contact.gender,
            isValidated: c.contact.isValidated,
            name: c.contact.name,
            position: c.contact.position,
            profileImageUrl: c.contact.profileImageUrl,
          }))
        });
      }
    }
    fetchTravellers();
  }, [value.artist?.artistId])

  const onAirlineChange = (airline: server.dto.Airline) => {
    const isFlightDesignatorOnlyNumbers = isFlightNumberOnly(value.flightDesignator);
    if (isFlightDesignatorOnlyNumbers) {
      const airlineCode = airline.iata || airline.icao || "";
      onChange({ airline, flightDesignator: `${airlineCode}${value.flightDesignator}` });
    } else {
      onChange({ airline });
    }
  }

  const onChange = (newData: Partial<FlightNewInfo>) => {
    setValue({ ...value, ...newData });
  }

  const onFlightSelectFromLookup = (flight: server.dto.ScheduledFlightFromFlightStats) => {
    setSelectedTab("commercial");
    setValue({
      ...value,
      airline: flight.airline,
      flightDesignator: flight.flightDesignator,

      arrivalAirport: flight.arrivalAirport,
      arrivalLocal: moment.utc(flight.arrivalLocal),
      arrivalTerminal: flight.arrivalTerminal,

      departureAirport: flight.departureAirport,
      departureLocal: moment.utc(flight.departureLocal),
      departureTerminal: flight.departureTerminal,
    });
  }

  const submit = () => {
    const instruction: server.dto.CreateFlightInstruction = {
      access: value.access,
      airlineId: value.airline?.airlineId || 0,
      arrivalAirportId: value.arrivalAirport.airportId,
      arrivalLocal: value.arrivalLocal.toDate(),
      arrivalTerminal: value.arrivalTerminal,
      artistId: value.artist?.artistId,
      passengers: value.passengers.map(p => ({
        baggage: "",
        bookingReference: value.bookingCode,
        contact: p.contactId,
        flightId: 0,
        frequentFlyerNumber: "",
        frequentFlyerProgramId: null,
        meal: "",
        seat: "",
        ticketNumber: "",
        travelClass: "unknown",
      })),
      capacity: value.capacity,
      confirmationNumber: value.confirmationNumber,
      departureAirportId: value.departureAirport.airportId,
      departureLocal: value.departureLocal.toDate(),
      departureTerminal: value.departureTerminal,
      flightDesignator: value.flightDesignator,
      flightType: selectedTab === "privateJet" ? "private" : "commercial",
      linkToShowId: planningContext.opts?.fromShowId || null,
      notes: value.notes,
      phase: value.phase.value,
      travelAgency: value.travelAgency,
    }

    return window.CoreApi.Flight.CreateFlight(instruction);
  }

  return <SidePanelNewSectionTemplate
    saveDisabled={selectedTab === "lookup"}
    onSave={(id) => {
      openFlightDetailSidePanel(id);
    }}
    title={RESX.Flight.NewFlight}
    id="new-flight-side-panel"
    trigger={null}
    top={<Tabs
      options={[
        { label: RESX.Flight.FlightLookup, value: "lookup" },
        { label: RESX.Flight.CommercialFlight, value: "commercial" },
        { label: RESX.Flight.PrivateJet, value: "privateJet" },
      ]}
      value={selectedTab}
      onChange={v => setSelectedTab(v)}
    />}
    submit={submit}
    fields={[{
      key: "flightLookup",
      label: "",
      hideLabel: true,
      hidden: selectedTab !== "lookup",
      renderEditNew: () => <FlightLookup
        airline={value.airline}
        departureDate={value.departureLocal}
        onAirlineChange={airline => onChange({ airline })}
        onDepartureDateChange={departureDate => onChange({ departureLocal: departureDate })}
        onSelect={onFlightSelectFromLookup}
      />
    }, {
      key: "artist",
      label: site.currentEnvironment.functionGroups.artist.singular,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          value={value.artist}
          onChange={artist => onChange({ artist })}
          validatorKey="artist"
          securityScope="limitByUserAccess"
          required
          canCreateNew={false}
        />
      </div>
    }, {
      key: "passengers",
      label: RESX.Flight.Passengers,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-fields-padding">
        <ContactMultiModalPicker
          value={value.passengers}
          onChange={passengers => onChange({ passengers })}
          canCreateNew={false}
          required={false}
          validatorKey="contacts"
        />
      </div>
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <AppointmentPhaseDropdown
        value={value.phase}
        onChange={phase => onChange({ phase })}
      />
    }, {
      key: "travelAgency",
      label: RESX.Flight.TravelAgency,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.flight.maxTravelAgencyLength}
        value={value.travelAgency}
        onChange={travelAgency => onChange({ travelAgency })}
      />
    }, {
      key: "notes",
      label: RESX.Note.Notes,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.flight.maxNotesLength}
        value={value.notes}
        onChange={notes => onChange({ notes })}
      />
    }, {
      key: "airline",
      hidden: selectedTab !== "commercial",
      label: RESX.Flight.Airline,
      renderEditNew: () => <AirlineDropdownInput
        value={value.airline}
        onChange={airline => onAirlineChange(airline)}
        isClearable={true}
        validatorKey="airline"
      />
    }, {
      key: "flightNumber",
      hidden: selectedTab !== "commercial",
      label: RESX.Flight.FlightNumber,
      renderEditNew: () => <FlightDesignatorInput
        placeholderAirline={value.airline?.iata || value.airline?.icao || ""}
        value={value.flightDesignator}
        validatorKey="flightNumber"
        onChange={flightDesignator => onChange({ flightDesignator })}
      />
    }, {
      key: "bookingCode",
      hidden: selectedTab !== "commercial",
      label: RESX.Flight.BookingCode,
      renderEditNew: () => <OptionalTextInput
        value={value.bookingCode}
        onChange={bookingCode => onChange({ bookingCode })}
        maxLength={serverReference.constants.flight.maxBookingReferenceLength}
      />
    }, {
      key: "access",
      label: RESX.SoUser.Access,
      hidden: selectedTab !== "privateJet",
      renderEditNew: () => <OptionalTextAreaInput
        maxLength={serverReference.constants.flight.maxPrivateJetAccessLength}
        value={value.access}
        onChange={access => onChange({ access })}
      />
    }, {
      key: "confirmationNumber",
      label: RESX.Flight.ConfirmationNumber,
      hidden: selectedTab !== "privateJet",
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.flight.maxPrivateJetConfirmationNumberLength}
        value={value.confirmationNumber}
        onChange={confirmationNumber => onChange({ confirmationNumber })}
      />,
    }, {
      key: "capacity",
      label: RESX.Venue.Capacity,
      hidden: selectedTab !== "privateJet",
      renderEditNew: () => <OptionalNumberInput
        width={100}
        value={value.capacity}
        labelContent="PAX"
        onChange={capacity => onChange({ capacity })}
        maxValue={1000}
        canBeEmpty
        decimalPoints={0}
        onEnterPressed={value => onChange({ capacity: value })}
      />
    }, {
      key: "departureArrivalLabel",
      label: "",
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <h3 style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">{RESX.Flight.Departure}</h3>
        <h3 style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">{RESX.Flight.Arrival}</h3>
      </div>
    }, {
      key: "date",
      label: RESX.DateTime.lblDate,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <DateTimePicker
          value={value.departureLocal}
          onChange={departureLocal => {
            const diff = departureLocal.diff(value.departureLocal, "minutes");
            const newArrivalLocal = value.arrivalLocal.clone().add(diff, "minutes");
            onChange({ departureLocal, arrivalLocal: newArrivalLocal })
          }}
          validatorKey="departureDate"
          minDate={moment(serverReference.constants.appointment.minDate)}
          maxDate={moment(serverReference.constants.appointment.maxDate)}
        />
        <DateTimePicker
          value={value.arrivalLocal}
          onChange={arrivalLocal => onChange({ arrivalLocal })}
          validatorKey="arrivalDate"
          minDate={value.departureLocal.clone().add(serverReference.constants.flight.maxArrivalBeforeDepartureHours, "hours")}
          maxDate={value.departureLocal.clone().add(serverReference.constants.flight.maxArrivalAfterDepartureHours, "hours")}
        />
      </div>
    }, {
      key: "airport",
      label: RESX.Flight.Airport,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <AirportDropdownInput
          value={value.departureAirport}
          onChange={departureAirport => onChange({ departureAirport })}
          validatorKey="departureAirport"
        />
        <AirportDropdownInput
          value={value.arrivalAirport}
          onChange={arrivalAirport => onChange({ arrivalAirport })}
          validatorKey="arrivalAirport"
        />
      </div>
    }, {
      key: "terminal",
      label: RESX.Flight.Terminal,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <OptionalTextInput
          width={60}
          value={value.departureTerminal}
          onChange={departureTerminal => onChange({ departureTerminal })}
          maxLength={serverReference.constants.flight.maxTerminalLength}
        />
        <OptionalTextInput
          width={60}
          value={value.arrivalTerminal}
          onChange={arrivalTerminal => onChange({ arrivalTerminal })}
          maxLength={serverReference.constants.flight.maxTerminalLength}
        />
      </div>
    }]}
  />
}

export const useOpenNewFlightSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewFlightSidePanel = useCallback(() => {
    openSidePanel(
      <NewFlightSidePanel />,
      "new-flight-side-panel",
      "large"
    );
  }, [openSidePanel]);

  return openNewFlightSidePanel;
}